import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  InputAdornment,
  Tabs,
  Tab,
  Divider,
  Stack,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import axios from 'axios';
import { API_URL } from './constants';
import { useAuth } from './AuthContext';
import { ProductImageBox } from './ProductImageBox';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const PREFERRED_DISPLAY_COLS = ['EAN', 'internalId', 'internal_id', 'Title', 'title', 'Brand', 'brand', 'Price', 'price'];

function TabPanel({ value, index, children }) {
  return value === index ? <Box>{children}</Box> : null;
}

/** Extract the product object from a possible_match JSON (flat or nested). */
function extractProduct(match) {
  if (!match) return null;
  return match.product ?? match;
}

/** Get the internal ID from a product object. */
function getProductId(product) {
  return product?.internal_id ?? product?.internalId ?? product?.id ?? '';
}

/** Format a value for table display. */
function fmtCell(v) {
  if (v == null) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

// ---------------------------------------------------------------------------
// Shared: generic CRUD table (used by tabs 2-4)
// ---------------------------------------------------------------------------

function CrudTable({ rows, deleteKey, onDelete, deleting, search, onSearchChange, onRefresh, loading, error }) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const columns = rows.length > 0 ? Object.keys(rows[0]) : [];

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => Object.values(r).some((v) => v != null && String(v).toLowerCase().includes(q)));
  }, [rows, search]);

  // Reset to page 0 whenever the filter changes
  const prevSearch = React.useRef(search);
  if (prevSearch.current !== search) {
    prevSearch.current = search;
    if (page !== 0) setPage(0);
  }

  const pageRows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  return (
    <Box>
      <Box display="flex" gap={2} alignItems="center" mb={2}>
        <TextField
          size="small"
          placeholder="Search…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ minWidth: 240 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
        />
        <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh} variant="outlined">
          Refresh
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
          {search.trim()
            ? <><strong>{filtered.length}</strong> filtered / {rows.length} total</>
            : <><strong>{rows.length}</strong> rows</>
          }
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Paper variant="outlined">
          <TableContainer>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{col}</TableCell>
                  ))}
                  <TableCell sx={{ fontWeight: 700, width: 80 }}>Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length + 1} align="center">
                      <Typography variant="body2" color="text.secondary" py={2}>No rows found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {pageRows.map((row, idx) => {
                  const rowKey = deleteKey(row);
                  const isDeleting = deleting === rowKey;
                  return (
                    <TableRow key={`${rowKey}-${idx}`} hover>
                      {columns.map((col) => (
                        <TableCell
                          key={col}
                          sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.78rem' }}
                        >
                          {fmtCell(row[col])}
                        </TableCell>
                      ))}
                      <TableCell>
                        <Tooltip title="Delete">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={isDeleting}
                              onClick={() => onDelete(row)}
                            >
                              {isDeleting ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[25, 50, 100, 250]}
          />
        </Paper>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function BatProductMatchingView() {
  const { isAdmin, projectKey } = useAuth();
  const canAccessProductMatching = isAdmin || projectKey === 'matching';

  const [activeTab, setActiveTab] = useState(0);

  // ── Tab 0: Snusbolaget Matching (existing state, unchanged) ────────────────
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [snusbolagetData, setSnusbolagetData] = useState([]);
  const [snusbolagetColumns, setSnusbolagetColumns] = useState([]);
  const [validMatchesData, setValidMatchesData] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [draftValue, setDraftValue] = useState('');
  const [draftCountry, setDraftCountry] = useState('SE');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  // ── Tab 1: Validate ────────────────────────────────────────────────────────
  const [vldRetailer, setVldRetailer] = useState('');
  const [vldBrand, setVldBrand] = useState('');
  const [vldSeries, setVldSeries] = useState('');
  const [vldProducts, setVldProducts] = useState([]);   // all unmatched products, loaded once
  const [vldLoaded, setVldLoaded] = useState(false);    // has the first fetch happened?
  const [vldLoading, setVldLoading] = useState(false);
  const [vldError, setVldError] = useState(null);
  const [vldActionLoading, setVldActionLoading] = useState(null);
  const [vldActionError, setVldActionError] = useState(null);
  const [vldShowEanForm, setVldShowEanForm] = useState(false);
  const [vldEan, setVldEan] = useState('');
  const [vldCountry, setVldCountry] = useState('SE');
  const [vldOffset, setVldOffset] = useState(0);
  const [vldRetailers, setVldRetailers] = useState([]);
  const [vldBrands, setVldBrands] = useState([]);
  const entitiesLoaded = useRef(false);

  // ── Tab 2: Valid Matches ───────────────────────────────────────────────────
  const [vmRows, setVmRows] = useState([]);
  const [vmLoading, setVmLoading] = useState(false);
  const [vmError, setVmError] = useState(null);
  const [vmDeleting, setVmDeleting] = useState(null);
  const [vmSearch, setVmSearch] = useState('');

  // ── Tab 3: Unknown Matches ─────────────────────────────────────────────────
  const [umRows, setUmRows] = useState([]);
  const [umLoading, setUmLoading] = useState(false);
  const [umError, setUmError] = useState(null);
  const [umDeleting, setUmDeleting] = useState(null);
  const [umSearch, setUmSearch] = useState('');

  // ── Tab 4: Wrong Category ──────────────────────────────────────────────────
  const [wcRows, setWcRows] = useState([]);
  const [wcLoading, setWcLoading] = useState(false);
  const [wcError, setWcError] = useState(null);
  const [wcDeleting, setWcDeleting] = useState(null);
  const [wcSearch, setWcSearch] = useState('');

  // ── Tab 0 derived state (unchanged) ───────────────────────────────────────
  const idColumn = useMemo(() => {
    for (const col of ['internal_id', 'internalId', 'EAN', 'ean', 'id', 'product_id']) {
      if (snusbolagetColumns.includes(col)) return col;
    }
    return snusbolagetColumns[0] || null;
  }, [snusbolagetColumns]);

  const displayColumns = useMemo(() => {
    if (!snusbolagetColumns.length) return [];
    const preferred = PREFERRED_DISPLAY_COLS.filter((c) => snusbolagetColumns.includes(c) && c !== idColumn);
    const rest = snusbolagetColumns.filter(
      (c) => !preferred.includes(c) && c !== idColumn &&
        !['updated_at', 'created_at', 'scraped_at', 'date', 'Date'].includes(c)
    );
    return [...preferred, ...rest].slice(0, 8);
  }, [snusbolagetColumns, idColumn]);

  const { matchByExactId } = useMemo(() => {
    const matchByExactId = {};
    validMatchesData.forEach((row) => {
      if (row.internalId != null) matchByExactId[String(row.internalId)] = row.referenceEan || '';
    });
    return { matchByExactId };
  }, [validMatchesData]);

  const getMatch = useMemo(() => (internalId) => {
    if (!internalId) return '';
    const idStr = String(internalId);
    if (matchByExactId[idStr]) return matchByExactId[idStr];
    const sub = validMatchesData.find((m) => m.referenceEan && String(m.referenceEan).includes(idStr));
    return sub ? sub.referenceEan : '';
  }, [matchByExactId, validMatchesData]);

  const filteredRows = useMemo(() => {
    let rows = snusbolagetData;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter((row) => Object.values(row).some((v) => v != null && String(v).toLowerCase().includes(q)));
    }
    if (filterStatus === 'matched') {
      rows = rows.filter((row) => { const id = idColumn ? String(row[idColumn] ?? '') : ''; return Boolean(getMatch(id)); });
    } else if (filterStatus === 'unmatched') {
      rows = rows.filter((row) => { const id = idColumn ? String(row[idColumn] ?? '') : ''; return !getMatch(id); });
    }
    return rows;
  }, [snusbolagetData, search, filterStatus, idColumn, getMatch]);

  const matchedCount = useMemo(
    () => snusbolagetData.filter((row) => { const id = idColumn ? String(row[idColumn] ?? '') : ''; return Boolean(getMatch(id)); }).length,
    [snusbolagetData, idColumn, getMatch]
  );

  // ── Loaders ────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async (date) => {
    setLoading(true);
    setError(null);
    try {
      const params = date ? { date } : {};
      const { data } = await axios.get(`${API_URL}/bat/product-matching`, { params, withCredentials: true });
      setAvailableDates(data.available_dates || []);
      setSelectedDate(data.date);
      setSnusbolagetData(data.snusbolaget || []);
      setSnusbolagetColumns(data.snusbolaget_columns || []);
      setValidMatchesData(data.valid_matches || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load product matching data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /** Fetch all unmatched products at once (called on initial load / refresh). */
  const loadAllProducts = useCallback(async () => {
    setVldLoading(true);
    setVldError(null);
    setVldShowEanForm(false);
    setVldEan('');
    setVldOffset(0);
    try {
      const params = { load_all: true };
      if (vldRetailer.trim()) params.retailer = vldRetailer.trim();
      if (vldBrand.trim()) params.brand = vldBrand.trim();
      if (vldSeries.trim()) params.productSeries = vldSeries.trim();
      const { data } = await axios.get(`${API_URL}/matching/recommend`, { params, withCredentials: true });
      const products = data.allProducts || [];
      setVldProducts(products);
      setVldLoaded(true);
      // Auto-pre-select the first product's algorithm top pick
      const bestEan = products[0]?.matchedReference?.ean;
      if (bestEan) { setVldEan(String(bestEan)); setVldShowEanForm(true); }
    } catch (err) {
      setVldError(err.response?.data?.detail || err.message || 'Failed to load products');
    } finally {
      setVldLoading(false);
    }
  }, [vldRetailer, vldBrand, vldSeries]);

  /** Navigate to a product index purely in state – zero database calls. */
  const goToProduct = useCallback((newIndex, products) => {
    const list = products ?? vldProducts;
    if (!list.length) return;
    const idx = Math.max(0, Math.min(list.length - 1, newIndex));
    setVldOffset(idx);
    setVldShowEanForm(false);
    setVldEan('');
    const bestEan = list[idx]?.matchedReference?.ean;
    if (bestEan) { setVldEan(String(bestEan)); setVldShowEanForm(true); }
  }, [vldProducts]);

  const loadUniqueEntities = useCallback(async () => {
    if (entitiesLoaded.current) return;
    try {
      const { data } = await axios.get(`${API_URL}/matching/unique-entities`, { withCredentials: true });
      if (data.retailers) setVldRetailers(data.retailers);
      if (data.brands) setVldBrands(data.brands);
      entitiesLoaded.current = true;
    } catch (_) {
      // non-critical: comboboxes still work as free-text
    }
  }, []);

  const loadValidMatches = useCallback(async () => {
    setVmLoading(true);
    setVmError(null);
    try {
      const { data } = await axios.get(`${API_URL}/matching/valid-matches`, { withCredentials: true });
      setVmRows(data.data || []);
    } catch (err) {
      setVmError(err.response?.data?.detail || err.message || 'Failed to load valid matches');
    } finally {
      setVmLoading(false);
    }
  }, []);

  const loadUnknownMatches = useCallback(async () => {
    setUmLoading(true);
    setUmError(null);
    try {
      const { data } = await axios.get(`${API_URL}/matching/unknown-matches`, { withCredentials: true });
      setUmRows(data.data || []);
    } catch (err) {
      setUmError(err.response?.data?.detail || err.message || 'Failed to load unknown matches');
    } finally {
      setUmLoading(false);
    }
  }, []);

  const loadWrongCategory = useCallback(async () => {
    setWcLoading(true);
    setWcError(null);
    try {
      const { data } = await axios.get(`${API_URL}/matching/wrong-category`, { withCredentials: true });
      setWcRows(data.data || []);
    } catch (err) {
      setWcError(err.response?.data?.detail || err.message || 'Failed to load wrong category');
    } finally {
      setWcLoading(false);
    }
  }, []);

  // Load tab data when first switching to it
  const loadedTabs = React.useRef(new Set([0]));
  useEffect(() => {
    if (loadedTabs.current.has(activeTab)) return;
    loadedTabs.current.add(activeTab);
    if (activeTab === 1) { loadUniqueEntities(); loadAllProducts(); }
    if (activeTab === 2) loadValidMatches();
    if (activeTab === 3) loadUnknownMatches();
    if (activeTab === 4) loadWrongCategory();
  }, [activeTab, loadAllProducts, loadValidMatches, loadUnknownMatches, loadWrongCategory, loadUniqueEntities]);

  // ── Tab 0: save handler (unchanged) ───────────────────────────────────────

  const handleSave = async (internalId) => {
    setSaving(internalId);
    setSaveError(null);
    try {
      const retailer = 'Snusbolaget (SE)';
      const brand = editingRow?.brand || editingRow?.Brand || '';
      await axios.post(`${API_URL}/bat/product-matching/save`, {
        internalId,
        referenceEan: draftValue,
        country: draftCountry,
        retailer,
        brand,
      }, { withCredentials: true });
      setValidMatchesData((prev) => {
        const exists = prev.some((r) => String(r.internalId) === String(internalId));
        if (exists) return prev.map((r) => String(r.internalId) === String(internalId) ? { ...r, referenceEan: draftValue, country: draftCountry } : r);
        return [...prev, { internalId, referenceEan: draftValue, country: draftCountry }];
      });
      setEditingId(null);
      setEditingRow(null);
      setDraftValue('');
      setDraftCountry('SE');
    } catch (err) {
      setSaveError(err.response?.data?.detail || err.message || 'Failed to save match');
    } finally {
      setSaving(null);
    }
  };

  // ── Tab 0: export unmatched products as XLSX ──────────────────────────────

  const handleExportXlsx = async () => {
    setExporting(true);
    setSaveError(null);
    try {
      const params = selectedDate ? { date: selectedDate } : {};
      const response = await axios.get(`${API_URL}/bat/product-matching/export-unmatched`, {
        params,
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `new_reference_products_${selectedDate || 'latest'}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSaveError('Failed to export XLSX: ' + (err.response?.data?.detail || err.message));
    } finally {
      setExporting(false);
    }
  };

  // ── Tab 1: validation actions ──────────────────────────────────────────────

  const postLog = async (action, product, referenceEan = '') => {
    try {
      await axios.post(`${API_URL}/matching/logs`, {
        action,
        retailer: product?.retailer || '',
        brand: product?.brand || '',
        country: vldCountry,
        internal_id: getProductId(product),
        reference_ean: referenceEan,
        client: 'pm',
      }, { withCredentials: true });
    } catch (_) { /* non-critical */ }
  };

  const handleValidate = useCallback(async (action) => {
    const product = extractProduct(vldProducts[vldOffset]);
    if (!product) return;
    const internalId = getProductId(product);
    setVldActionLoading(action);
    setVldActionError(null);
    try {
      if (action === 'valid') {
        await axios.post(`${API_URL}/matching/valid-matches`, {
          retailer: product.retailer || '',
          brand: product.brand || '',
          country: vldCountry,
          internalId,
          referenceEan: vldEan,
        }, { withCredentials: true });
        await postLog('valid_match', product, vldEan);
      } else if (action === 'unknown') {
        await axios.post(`${API_URL}/matching/unknown-matches`, {
          retailer: product.retailer || '',
          brand: product.brand || '',
          country: vldCountry,
          internalId,
          features: product.features || '',
        }, { withCredentials: true });
        await postLog('unknown_match', product);
      } else if (action === 'wrong_category') {
        await axios.post(`${API_URL}/matching/wrong-category`, {
          retailer: product.retailer || '',
          brand: product.brand || '',
          country: vldCountry,
          internalId,
        }, { withCredentials: true });
        await postLog('wrong_category', product);
      }
      // Remove classified product from local list – no database request needed
      const nextProducts = vldProducts.filter((_, i) => i !== vldOffset);
      setVldProducts(nextProducts);
      goToProduct(vldOffset, nextProducts);   // stays at same index (next product slides in)
    } catch (err) {
      setVldActionError(err.response?.data?.detail || err.message || 'Action failed');
    } finally {
      setVldActionLoading(null);
    }
  }, [vldProducts, vldOffset, vldCountry, vldEan, goToProduct]);

  // ── Tab 2-4: delete handlers ───────────────────────────────────────────────

  const handleDeleteVm = async (row) => {
    const key = `${row.internalId}-${row.referenceEan}-${row.retailer}`;
    setVmDeleting(key);
    try {
      await axios.delete(`${API_URL}/matching/valid-matches`, {
        data: { internalId: row.internalId, referenceEan: row.referenceEan, retailer: row.retailer },
        withCredentials: true,
      });
      setVmRows((prev) => prev.filter((r) => !(r.internalId === row.internalId && r.referenceEan === row.referenceEan && r.retailer === row.retailer)));
    } catch (err) {
      setVmError(err.response?.data?.detail || err.message || 'Delete failed');
    } finally {
      setVmDeleting(null);
    }
  };

  const handleDeleteUm = async (row) => {
    const key = `${row.internalId}-${row.retailer}`;
    setUmDeleting(key);
    try {
      await axios.delete(`${API_URL}/matching/unknown-matches`, {
        data: { internalId: row.internalId, retailer: row.retailer },
        withCredentials: true,
      });
      setUmRows((prev) => prev.filter((r) => !(r.internalId === row.internalId && r.retailer === row.retailer)));
    } catch (err) {
      setUmError(err.response?.data?.detail || err.message || 'Delete failed');
    } finally {
      setUmDeleting(null);
    }
  };

  const handleDeleteWc = async (row) => {
    const key = `${row.internalId}-${row.retailer}`;
    setWcDeleting(key);
    try {
      await axios.delete(`${API_URL}/matching/wrong-category`, {
        data: { internalId: row.internalId, retailer: row.retailer },
        withCredentials: true,
      });
      setWcRows((prev) => prev.filter((r) => !(r.internalId === row.internalId && r.retailer === row.retailer)));
    } catch (err) {
      setWcError(err.response?.data?.detail || err.message || 'Delete failed');
    } finally {
      setWcDeleting(null);
    }
  };

  // ── Guards ─────────────────────────────────────────────────────────────────

  if (!canAccessProductMatching) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Product matching requires admin access or the matching project password.
        </Alert>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box p={3}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        BAT Product Matching
      </Typography>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Snusbolaget" />
        <Tab label="Validate" />
        <Tab label={`Valid Matches${vmRows.length ? ` (${vmRows.length})` : ''}`} />
        <Tab label={`Unknown Matches${umRows.length ? ` (${umRows.length})` : ''}`} />
        <Tab label={`Wrong Category${wcRows.length ? ` (${wcRows.length})` : ''}`} />
      </Tabs>

      {/* ================================================================== */}
      {/* TAB 0 — Snusbolaget Matching (existing, unchanged)                  */}
      {/* ================================================================== */}
      <TabPanel value={activeTab} index={0}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Source tables: <code>ecommerce_prod.snusbolaget_se</code> ×{' '}
          <code>database_prod.valid_matches</code>. Edit the{' '}
          <strong>referenceEan</strong> column to update a match; changes are written directly to the database.
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              Loading snusbolaget_se and valid_matches…
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <>
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Date (last day of DB)</InputLabel>
                    <Select
                      value={selectedDate}
                      label="Date (last day of DB)"
                      onChange={(e) => { setSelectedDate(e.target.value); fetchData(e.target.value); }}
                    >
                      {availableDates.map((d) => (
                        <MenuItem key={d} value={d}>
                          {d}
                          {d === availableDates[0] && <Chip label="latest" size="small" color="primary" sx={{ ml: 1 }} />}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                      <MenuItem value="all">All ({snusbolagetData.length})</MenuItem>
                      <MenuItem value="matched">Matched ({matchedCount})</MenuItem>
                      <MenuItem value="unmatched">Unmatched ({snusbolagetData.length - matchedCount})</MenuItem>
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    placeholder="Search…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ minWidth: 220 }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
                    Showing <strong>{filteredRows.length}</strong> / {snusbolagetData.length} products
                  </Typography>

                  <Tooltip title={`Download ${snusbolagetData.length - matchedCount} unmatched products as XLSX (reference template)`}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={exporting ? <CircularProgress size={14} /> : <DownloadIcon fontSize="small" />}
                        onClick={handleExportXlsx}
                        disabled={exporting || snusbolagetData.length === 0}
                      >
                        {exporting ? 'Exporting…' : `Export unmatched (${snusbolagetData.length - matchedCount})`}
                      </Button>
                    </span>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>

            {saveError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>{saveError}</Alert>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    {idColumn && <TableCell sx={{ fontWeight: 700 }}>{idColumn}</TableCell>}
                    {displayColumns.map((col) => (
                      <TableCell key={col} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{col}</TableCell>
                    ))}
                    <TableCell sx={{ fontWeight: 700 }}>referenceEan</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={displayColumns.length + 4} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No products found.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {filteredRows.map((row, idx) => {
                    const internalId = idColumn ? String(row[idColumn] ?? '') : String(idx);
                    const currentRef = getMatch(internalId);
                    const isExactMatch = Boolean(matchByExactId[internalId]);
                    const isSubstringMatch = !isExactMatch && Boolean(currentRef);
                    const isEditing = editingId === internalId;
                    const isSavingThis = saving === internalId;
                    const totalCols = 1 + (idColumn ? 1 : 0) + displayColumns.length + 2;

                    return (
                      <React.Fragment key={`${internalId}-${idx}`}>
                        <TableRow hover sx={{ bgcolor: isEditing ? 'action.selected' : undefined }}>
                          <TableCell>
                            <Tooltip title={isExactMatch ? 'Matched (exact)' : isSubstringMatch ? 'Suggested (substring)' : 'Unmatched'}>
                              {isExactMatch ? (
                                <CheckCircleOutlineIcon fontSize="small" color="success" />
                              ) : isSubstringMatch ? (
                                <CheckCircleOutlineIcon fontSize="small" sx={{ color: 'warning.main' }} />
                              ) : (
                                <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
                              )}
                            </Tooltip>
                          </TableCell>
                          {idColumn && (
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{row[idColumn] ?? '—'}</TableCell>
                          )}
                          {displayColumns.map((col) => (
                            <TableCell key={col} sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {row[col] != null ? String(row[col]) : '—'}
                            </TableCell>
                          ))}
                          <TableCell sx={{ minWidth: 180 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', color: isExactMatch ? 'success.main' : isSubstringMatch ? 'warning.main' : 'text.disabled' }}>
                              {currentRef || '—'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {isExactMatch ? (
                              <Tooltip title="Edit existing match">
                                <IconButton size="small" onClick={() => { setEditingId(internalId); setEditingRow(row); setDraftValue(currentRef); const er = validMatchesData.find((r) => String(r.internalId) === internalId); setDraftCountry(er?.country || 'SE'); }}>
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title={isSubstringMatch ? `Add suggested match: ${currentRef}` : 'Add to valid_matches'}>
                                <IconButton size="small" color="primary" onClick={() => { setEditingId(internalId); setEditingRow(row); setDraftValue(currentRef); setDraftCountry('SE'); }}>
                                  <AddCircleOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                        {isEditing && (
                          <TableRow sx={{ bgcolor: 'action.selected' }}>
                            <TableCell colSpan={totalCols} sx={{ py: 1.5, px: 3, borderBottom: '2px solid', borderColor: 'primary.main' }}>
                              <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                                <Typography variant="body2" color="text.secondary">
                                  Map&nbsp;<Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{internalId}</Box>&nbsp;→
                                </Typography>
                                <TextField
                                  size="small" label="referenceEan" value={draftValue} onChange={(e) => setDraftValue(e.target.value)}
                                  placeholder="Enter referenceEan" autoFocus sx={{ width: 220 }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') handleSave(internalId); if (e.key === 'Escape') { setEditingId(null); setEditingRow(null); setDraftValue(''); setDraftCountry('SE'); } }}
                                />
                                <TextField
                                  size="small" label="country" value={draftCountry} onChange={(e) => setDraftCountry(e.target.value)}
                                  sx={{ width: 80 }}
                                  onKeyDown={(e) => { if (e.key === 'Escape') { setEditingId(null); setEditingRow(null); setDraftValue(''); setDraftCountry('SE'); } }}
                                />
                                <Button size="small" variant="contained" startIcon={isSavingThis ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />} onClick={() => handleSave(internalId)} disabled={isSavingThis || !draftValue.trim()}>
                                  Save
                                </Button>
                                <Button size="small" variant="outlined" startIcon={<CloseIcon fontSize="small" />} onClick={() => { setEditingId(null); setEditingRow(null); setDraftValue(''); setDraftCountry('SE'); }}>
                                  Cancel
                                </Button>
                                {isSubstringMatch && !isExactMatch && (
                                  <Typography variant="caption" color="text.secondary">Suggested from substring match</Typography>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </TabPanel>

      {/* ================================================================== */}
      {/* TAB 1 — Validate (workflow: possible_matches one by one)            */}
      {/* ================================================================== */}
      <TabPanel value={activeTab} index={1}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Work through unmatched products from <code>philip_morris_prod.possible_matches</code>. Classify each as
          a valid match, unknown, or wrong category.
        </Typography>

        {/* Filter row */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
              <Autocomplete
                freeSolo
                options={vldRetailers}
                value={vldRetailer}
                onInputChange={(_, v) => setVldRetailer(v ?? '')}
                onChange={(_, v) => setVldRetailer(v ?? '')}
                sx={{ minWidth: 180 }}
                renderInput={(params) => <TextField {...params} size="small" label="Retailer" placeholder="All" />}
              />
              <Autocomplete
                freeSolo
                options={vldBrands}
                value={vldBrand}
                onInputChange={(_, v) => setVldBrand(v ?? '')}
                onChange={(_, v) => setVldBrand(v ?? '')}
                sx={{ minWidth: 180 }}
                renderInput={(params) => <TextField {...params} size="small" label="Brand" placeholder="All" />}
              />
              <TextField
                size="small" label="Product Series" value={vldSeries} onChange={(e) => setVldSeries(e.target.value)}
                sx={{ minWidth: 180 }} placeholder="All"
              />
              <Button
                variant="outlined" size="small" startIcon={<RefreshIcon />}
                onClick={() => { loadedTabs.current.add(1); loadUniqueEntities(); loadAllProducts(); }}
              >
                Load / Refresh
              </Button>
              {vldLoaded && (
                <>
                  <Chip
                    label={`${vldProducts.length} unmatched`}
                    color={vldProducts.length > 0 ? 'warning' : 'success'}
                    size="small"
                  />
                  {vldProducts.length > 0 && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Tooltip title="Previous product (no reload)">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => goToProduct(vldOffset - 1)}
                            disabled={vldOffset === 0 || vldLoading}
                          >
                            <NavigateBeforeIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Typography variant="body2" sx={{ minWidth: 60, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>
                        {vldOffset + 1} / {vldProducts.length}
                      </Typography>
                      <Tooltip title="Next product (no reload)">
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => goToProduct(vldOffset + 1)}
                            disabled={vldOffset >= vldProducts.length - 1 || vldLoading}
                          >
                            <NavigateNextIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </CardContent>
        </Card>

        {vldError && <Alert severity="error" sx={{ mb: 2 }}>{vldError}</Alert>}
        {vldActionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setVldActionError(null)}>{vldActionError}</Alert>}

        {vldLoading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : !vldLoaded ? null
          : vldProducts.length === 0 ? (
            <Alert severity="success" icon={<CheckCircleOutlineIcon />}>
              All products have been matched! No unmatched products remaining{vldRetailer || vldBrand || vldSeries ? ' for the current filters' : ''}.
            </Alert>
          ) : (
            <ValidateProductCard
              match={vldProducts[vldOffset]}
              country={vldCountry}
              onCountryChange={setVldCountry}
              showEanForm={vldShowEanForm}
              onShowEanForm={() => setVldShowEanForm(true)}
              ean={vldEan}
              onEanChange={setVldEan}
              onValidate={handleValidate}
              onSkip={() => goToProduct(vldOffset + 1)}
              actionLoading={vldActionLoading}
            />
          )
        }
      </TabPanel>

      {/* ================================================================== */}
      {/* TAB 2 — Valid Matches                                               */}
      {/* ================================================================== */}
      <TabPanel value={activeTab} index={2}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Browse and delete rows in <code>philip_morris_prod.valid_matches</code>.
        </Typography>
        <CrudTable
          rows={vmRows}
          loading={vmLoading}
          error={vmError}
          search={vmSearch}
          onSearchChange={setVmSearch}
          onRefresh={loadValidMatches}
          deleting={vmDeleting}
          deleteKey={(r) => `${r.internalId}-${r.referenceEan}-${r.retailer}`}
          onDelete={handleDeleteVm}
        />
      </TabPanel>

      {/* ================================================================== */}
      {/* TAB 3 — Unknown Matches                                             */}
      {/* ================================================================== */}
      <TabPanel value={activeTab} index={3}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Browse and delete rows in <code>philip_morris_prod.unknown_matches</code>.
        </Typography>
        <CrudTable
          rows={umRows}
          loading={umLoading}
          error={umError}
          search={umSearch}
          onSearchChange={setUmSearch}
          onRefresh={loadUnknownMatches}
          deleting={umDeleting}
          deleteKey={(r) => `${r.internalId}-${r.retailer}`}
          onDelete={handleDeleteUm}
        />
      </TabPanel>

      {/* ================================================================== */}
      {/* TAB 4 — Wrong Category                                              */}
      {/* ================================================================== */}
      <TabPanel value={activeTab} index={4}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Browse and delete rows in <code>philip_morris_prod.wrong_category</code>.
        </Typography>
        <CrudTable
          rows={wcRows}
          loading={wcLoading}
          error={wcError}
          search={wcSearch}
          onSearchChange={setWcSearch}
          onRefresh={loadWrongCategory}
          deleting={wcDeleting}
          deleteKey={(r) => `${r.internalId}-${r.retailer}`}
          onDelete={handleDeleteWc}
        />
      </TabPanel>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ValidateProductCard — mirrors matching_validator's ProductComparisonDisplay
// ---------------------------------------------------------------------------

/** Safely parse a value that might itself be a JSON string (double-encoded). */
function safeParse(value) {
  if (value == null) return value;
  if (typeof value === 'object') return value;
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch (_) { return value; }
  }
  return value;
}

function ValidateProductCard({ match, country, onCountryChange, showEanForm, onShowEanForm, ean, onEanChange, onValidate, onSkip, actionLoading }) {
  // Guard: if match itself arrived as a string (double-encoded), parse it first
  const parsedMatch = safeParse(match);
  const product = extractProduct(parsedMatch);
  const internalId = getProductId(product);

  const imageUrl    = product?.image ?? product?.imageUrl ?? product?.img ?? null;
  const productUrl  = product?.url   ?? product?.link     ?? null;
  const title       = product?.title ?? product?.name     ?? `#${internalId}`;
  const brand       = product?.brand  ?? '';
  const retailer    = product?.retailer ?? '';
  const modelSeries = product?.modelSeries ?? product?.model_series ?? product?.series ?? '';

  // similarProductsNames is the list from the matching algorithm (sorted by Score desc, up to 200)
  const similarProductsNames = parsedMatch?.similarProductsNames ?? [];
  // matchedReference is the algorithm's automatic top pick
  const matchedReference = parsedMatch?.matchedReference ?? null;

  // Selected reference product (found by EAN in similarProductsNames, fallback to matchedReference)
  const selectedRef = ean
    ? similarProductsNames.find((pm) => {
        const pmEan = pm?.ean ?? pm?.referenceEan ?? '';
        return pmEan && String(pmEan) === String(ean);
      }) ?? (matchedReference && String(matchedReference.ean) === String(ean) ? matchedReference : null)
    : null;

  const refImage    = selectedRef?.image ?? selectedRef?.Image ?? selectedRef?.imageUrl ?? null;
  const refTitle    = selectedRef?.name  ?? selectedRef?.title ?? '';
  const refBrand    = selectedRef?.brand ?? selectedRef?.Brand ?? '';
  const refSeries   = selectedRef?.modelSeries ?? selectedRef?.['model series'] ?? selectedRef?.model_series ?? '';
  const refCategory = selectedRef?.category ?? selectedRef?.Category ?? '';
  const refUrl      = selectedRef?.url ?? selectedRef?.Url ?? selectedRef?.link ?? null;
  const refManufacturer = selectedRef?.manufacturer ?? selectedRef?.Manufacturer ?? '';
  const refScore    = typeof selectedRef?.Score === 'number' ? selectedRef.Score : null;

  // Key product fields to show as a tidy list
  const SHOW_KEYS = ['internal_id', 'internalId', 'brand', 'retailer', 'modelSeries',
                     'model_series', 'series', 'features', 'format', 'nicotine',
                     'strength', 'category', 'country'];
  const skipFull = new Set(['image', 'imageUrl', 'img', 'url', 'link',
                            'title', 'name', 'possible_match', 'ai_features',
                            'similarProductsNames', 'similarProductsFeatures']);
  const shownKeys = SHOW_KEYS.filter(k => product?.[k] != null && product[k] !== '');
  const allScalarKeys = Object.keys(product || {}).filter(k =>
    !skipFull.has(k) && !shownKeys.includes(k) &&
    product[k] != null && product[k] !== '' && typeof product[k] !== 'object'
  );
  const fieldKeys = [...shownKeys, ...allScalarKeys];

  const aiFeatures = (product?.ai_features && typeof product.ai_features === 'object')
    ? product.ai_features : null;

  const isActionLoading = (a) => actionLoading === a;

  return (
    <Card variant="outlined">
      {/* ── Top bar: title + chips + open-link ── */}
      <Box sx={{ px: 2, pt: 2, pb: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3 }}>
            {title}
          </Typography>
          <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" useFlexGap>
            {brand       && <Chip label={brand}       size="small" color="primary"   variant="outlined" />}
            {retailer    && <Chip label={retailer}    size="small" color="default"   variant="outlined" />}
            {modelSeries && <Chip label={modelSeries} size="small" color="secondary" variant="outlined" />}
            {internalId  && (
              <Chip label={`ID: ${internalId}`} size="small" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100' }} />
            )}
          </Stack>
        </Box>
        {productUrl && (
          <Tooltip title="Open product page">
            <IconButton size="small" component="a" href={productUrl} target="_blank" rel="noopener noreferrer">
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider />

      {/* ── Side-by-side: retailer product | reference product ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0, minHeight: 220 }}>

        {/* LEFT — Retailer product */}
        <Box sx={{ p: 2, borderRight: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Retailer product
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <ProductImageBox src={imageUrl} alt={title} size={160} />
            <Box flex={1} minWidth={160}>
              <Table size="small" sx={{ '& td': { borderBottom: 'none', py: 0.3 } }}>
                <TableBody>
                  {fieldKeys.map((k) => (
                    <TableRow key={k}>
                      <TableCell sx={{ color: 'text.secondary', width: 120, fontSize: '0.75rem', fontWeight: 600, pr: 1 }}>
                        {k}
                      </TableCell>
                      <TableCell sx={{
                        fontFamily: ['internal_id', 'internalId'].includes(k) ? 'monospace' : 'inherit',
                        fontSize: '0.8rem',
                      }}>
                        {String(product[k])}
                      </TableCell>
                    </TableRow>
                  ))}
                  {aiFeatures && Object.entries(aiFeatures).filter(([, v]) => v != null).map(([k, v]) => (
                    <TableRow key={`ai_${k}`}>
                      <TableCell sx={{ color: 'info.main', width: 120, fontSize: '0.75rem', fontWeight: 600, pr: 1 }}>{k}</TableCell>
                      <TableCell sx={{ fontSize: '0.8rem' }}>{String(v)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        </Box>

        {/* RIGHT — Reference product (shown when EAN is selected, else placeholder) */}
        <Box sx={{ p: 2, bgcolor: selectedRef ? 'success.50' : 'grey.50' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Reference product
            </Typography>
            {ean && (
              <Chip label={ean} size="small" color="success" variant="outlined" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }} />
            )}
            {refScore !== null && (
              <Chip
                label={`Score: ${refScore}`}
                size="small"
                color={refScore >= 80 ? 'success' : refScore >= 60 ? 'warning' : 'default'}
                variant="filled"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
            {refUrl && (
              <Tooltip title="Open reference page">
                <IconButton size="small" component="a" href={refUrl} target="_blank" rel="noopener noreferrer">
                  <OpenInNewIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {!selectedRef ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, color: 'text.disabled' }}>
              <RadioButtonUncheckedIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
              <Typography variant="body2" color="text.disabled">
                Select a match from the suggestions below
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <ProductImageBox src={refImage} alt={refTitle} size={160} />
              <Box flex={1} minWidth={160}>
                {refTitle && (
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 1, lineHeight: 1.3 }}>
                    {refTitle}
                  </Typography>
                )}
                <Table size="small" sx={{ '& td': { borderBottom: 'none', py: 0.3 } }}>
                  <TableBody>
                    {refBrand && (
                      <TableRow>
                        <TableCell sx={{ color: 'text.secondary', width: 110, fontSize: '0.75rem', fontWeight: 600 }}>brand</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{refBrand}</TableCell>
                      </TableRow>
                    )}
                    {refManufacturer && (
                      <TableRow>
                        <TableCell sx={{ color: 'text.secondary', width: 110, fontSize: '0.75rem', fontWeight: 600 }}>manufacturer</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{refManufacturer}</TableCell>
                      </TableRow>
                    )}
                    {refSeries && (
                      <TableRow>
                        <TableCell sx={{ color: 'text.secondary', width: 110, fontSize: '0.75rem', fontWeight: 600 }}>model series</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{refSeries}</TableCell>
                      </TableRow>
                    )}
                    {refCategory && (
                      <TableRow>
                        <TableCell sx={{ color: 'text.secondary', width: 110, fontSize: '0.75rem', fontWeight: 600 }}>category</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem' }}>{refCategory}</TableCell>
                      </TableRow>
                    )}
                    {ean && (
                      <TableRow>
                        <TableCell sx={{ color: 'text.secondary', width: 110, fontSize: '0.75rem', fontWeight: 600 }}>EAN</TableCell>
                        <TableCell sx={{ fontSize: '0.8rem', fontFamily: 'monospace' }}>{ean}</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Divider />

      {/* ── Suggestions strip ── */}
      {similarProductsNames.length > 0 && (
        <>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Suggested matches ({similarProductsNames.length}) — click to select
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {similarProductsNames.map((pm, i) => {
                const pmEan   = pm?.ean ?? pm?.referenceEan ?? '';
                const pmTitle = pm?.name ?? pm?.title ?? '';
                const pmImage = pm?.Image ?? pm?.image ?? null;
                const pmScore = typeof pm?.Score === 'number' ? pm.Score : null;
                const isSelected = ean && String(ean) === String(pmEan);
                const isBestMatch = matchedReference && String(matchedReference.ean) === String(pmEan);
                return (
                  <Box
                    key={i}
                    onClick={() => onEanChange(isSelected ? '' : pmEan)}
                    sx={{
                      display: 'flex', alignItems: 'flex-start', gap: 1, p: 0.75,
                      border: '2px solid',
                      borderColor: isSelected ? 'success.main' : isBestMatch ? 'primary.light' : 'divider',
                      borderRadius: 1, cursor: 'pointer',
                      bgcolor: isSelected ? 'success.50' : isBestMatch ? 'primary.50' : 'background.paper',
                      minWidth: 160, maxWidth: 240,
                      '&:hover': { borderColor: isSelected ? 'success.dark' : 'primary.main', bgcolor: isSelected ? 'success.100' : 'action.hover' },
                    }}
                  >
                    {pmImage && (
                      <Box
                        component="img" src={pmImage} alt={pmTitle}
                        sx={{ width: 40, height: 40, objectFit: 'contain', flexShrink: 0, borderRadius: 0.5, mt: 0.25 }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <Box overflow="hidden" flex={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                        {pmScore !== null && (
                          <Chip
                            label={pmScore}
                            size="small"
                            color={pmScore >= 80 ? 'success' : pmScore >= 60 ? 'warning' : 'default'}
                            sx={{ height: 16, fontSize: '0.65rem', fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        )}
                        {isBestMatch && !isSelected && (
                          <Chip label="top pick" size="small" color="primary" variant="outlined"
                            sx={{ height: 16, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }} />
                        )}
                        {isSelected && <CheckCircleOutlineIcon sx={{ fontSize: 14, color: 'success.main', flexShrink: 0 }} />}
                      </Box>
                      {pmTitle && (
                        <Typography variant="caption" display="block" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isSelected ? 700 : 400 }}>
                          {pmTitle}
                        </Typography>
                      )}
                      {pmEan && (
                        <Typography variant="caption" color="text.secondary" fontFamily="monospace" display="block">
                          {pmEan}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
          <Divider />
        </>
      )}

      {/* ── Action bar ── */}
      <Box sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <TextField
            size="small" label="Country" value={country}
            onChange={(e) => onCountryChange(e.target.value)}
            sx={{ width: 80 }}
          />

          {/* Valid Match button / inline EAN form */}
          {!showEanForm ? (
            <Button
              variant="contained" color="success" size="medium"
              onClick={onShowEanForm} disabled={!!actionLoading}
              startIcon={<CheckCircleOutlineIcon />}
              sx={{ minWidth: 140 }}
            >
              ✓ Valid Match
            </Button>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <TextField
                size="small" label="referenceEan" value={ean}
                onChange={(e) => onEanChange(e.target.value)}
                placeholder="Enter or select from suggestions"
                autoFocus sx={{ minWidth: 260 }}
                onKeyDown={(e) => { if (e.key === 'Enter' && ean.trim()) onValidate('valid'); }}
              />
              <Button
                variant="contained" color="success" size="medium"
                onClick={() => onValidate('valid')}
                disabled={isActionLoading('valid') || !ean.trim()}
                startIcon={isActionLoading('valid') ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              >
                Confirm
              </Button>
              <Button size="small" variant="text" onClick={() => onEanChange('')}>Clear</Button>
            </Stack>
          )}

          <Button
            variant="outlined" color="warning" size="medium"
            onClick={() => onValidate('unknown')} disabled={!!actionLoading}
            startIcon={isActionLoading('unknown') ? <CircularProgress size={16} /> : <RadioButtonUncheckedIcon />}
            sx={{ minWidth: 130 }}
          >
            Missing ref
          </Button>

          <Button
            variant="outlined" color="error" size="medium"
            onClick={() => onValidate('wrong_category')} disabled={!!actionLoading}
            startIcon={isActionLoading('wrong_category') ? <CircularProgress size={16} /> : <WarningAmberIcon />}
            sx={{ minWidth: 150 }}
          >
            Wrong Category
          </Button>

          <Button
            variant="text" color="inherit" size="medium"
            onClick={onSkip} disabled={!!actionLoading}
            startIcon={<SkipNextIcon />}
            sx={{ minWidth: 90, color: 'text.secondary' }}
          >
            Skip
          </Button>
        </Stack>
      </Box>
    </Card>
  );
}

export default BatProductMatchingView;
